import * as XLSX from 'xlsx';

export const SOCIETY_COLS = [
  'society_name', 'location', 'city', 'state', 'pincode', 'country',
  'type', 'flat_no_format', 'latlong',
  'account_number', 'account_ifsc',
];

export const BLOCK_COLS = ['block_name', 'number_of_floors', 'custom_letter'];

export const FLAT_COLS = [
  'block', 'flat_number', 'floor', 'area_sqft', 'flat_type',
  'user_name', 'user_phone', 'user_email', 'role_in_flat', 'society_role',
  'two_wheelers', 'four_wheelers',
];

export interface OnboardExcelSociety {
  name: string;
  location: string;
  city: string;
  state?: string;
  pincode?: string;
  country?: string;
  type?: string;
  flatNoFormat?: string;
  latlong?: string;
  account_number?: string;
  account_ifsc?: string;
}

export interface OnboardExcelBlock {
  blockName: string;
  numberOfFloors?: number;
  customLetter?: string;
}

export interface OnboardExcelFlat {
  block: string;
  flat_number: string;
  floor?: number | string;
  area?: number;
  flat_type?: string;
  user_name?: string;
  user_phone?: string;
  user_email?: string;
  role_in_flat?: string;
  society_role?: string;
  two_wheelers?: string;
  four_wheelers?: string;
}

export interface OnboardExcelPayload {
  society: OnboardExcelSociety;
  blocks: OnboardExcelBlock[];
  flats: OnboardExcelFlat[];
}

const INSTRUCTIONS_ROWS: string[][] = [
  ['Sheet', 'Column', 'Required?', 'Notes'],
  ['Society', 'society_name', 'YES', 'Full name of the society'],
  ['Society', 'location', 'YES', 'Street address'],
  ['Society', 'city', 'YES', ''],
  ['Society', 'state', 'YES', ''],
  ['Society', 'pincode', 'YES', ''],
  ['Society', 'country', 'no', 'Default: India'],
  ['Society', 'type', 'no', 'multiStory (default) or individualVilla'],
  ['Society', 'flat_no_format', 'no', 'number | letter | numberLetter | letterNumber | customLetterNumber | numberCustomLetter'],
  ['Society', 'latlong', 'no', 'GPS co-ordinates e.g. 19.1136,72.8697'],
  ['Society', 'account_number', 'no', 'Society bank account number'],
  ['Society', 'account_ifsc', 'no', 'Bank IFSC code'],
  [''],
  ['Blocks', 'block_name', 'YES', 'Block/Tower name e.g. A, Tower-1'],
  ['Blocks', 'number_of_floors', 'YES', 'Total number of floors in this block'],
  ['Blocks', 'custom_letter', 'no', 'Custom letter prefix for flat numbering e.g. A'],
  [''],
  ['Flats', 'block', 'YES', 'Must match a block_name in the Blocks sheet'],
  ['Flats', 'flat_number', 'YES', 'Flat identifier e.g. A101'],
  ['Flats', 'floor', 'no', 'Floor number (integer)'],
  ['Flats', 'area_sqft', 'no', 'Flat area in sq.ft'],
  ['Flats', 'flat_type', 'no', 'Optional e.g. 2 BHK, 3 BHK, Villa. Spacing/case variants count as one type (2BHK = 2 BHK)'],
  ['Flats', 'user_name', 'no', 'Leave blank if flat is vacant'],
  ['Flats', 'user_phone', 'no', 'Mobile number'],
  ['Flats', 'user_email', 'no', 'Email address'],
  ['Flats', 'role_in_flat', 'no', 'owner (default), tenant, or family_member'],
  ['Flats', 'role_in_flat (export)', 'no', 'One row per member: owner, tenant, family_member (linked to owner or tenant)'],
  ['Flats', 'society_role', 'no', 'president | treasurer | body_member | normal_member | guard'],
  ['Flats', 'two_wheelers', 'no', 'Comma-separated 2-wheeler numbers e.g. MH01AB1234,MH01CD5678'],
  ['Flats', 'four_wheelers', 'no', 'Comma-separated car numbers e.g. MH02XY9876'],
  [''],
  ['NOTES:'],
  ['• Blocks sheet is required — each block must have a row here.'],
  ['• Flat "block" column must exactly match a block_name from the Blocks sheet.'],
  ['• Vacant flats: leave user_name, user_phone and user_email all blank.'],
  ['• Vehicles require a linked user — cannot be added to vacant flats.'],
  ['• Multiple vehicle numbers: separate with comma e.g. MH01AB1234,MH01CD5678'],
  ['• Existing users are matched by phone or email and linked instead of re-created.'],
  ['• New users are created with a temporary password; they reset via OTP on first login.'],
];

export function buildOnboardWorkbook(payload: OnboardExcelPayload): XLSX.WorkBook {
  const s = payload.society;
  const societyRow = [
    s.name,
    s.location,
    s.city,
    s.state ?? '',
    s.pincode ?? '',
    s.country ?? 'India',
    s.type ?? 'multiStory',
    s.flatNoFormat ?? '',
    s.latlong ?? '',
    s.account_number ?? '',
    s.account_ifsc ?? '',
  ];

  const blockRows = payload.blocks.map(b => [
    b.blockName,
    b.numberOfFloors ?? 1,
    b.customLetter ?? '',
  ]);

  const flatRows = payload.flats.map(f => [
    f.block,
    f.flat_number,
    f.floor ?? '',
    f.area ?? '',
    f.flat_type ?? '',
    f.user_name ?? '',
    f.user_phone ?? '',
    f.user_email ?? '',
    f.role_in_flat ?? '',
    f.society_role ?? '',
    f.two_wheelers ?? '',
    f.four_wheelers ?? '',
  ]);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([SOCIETY_COLS, societyRow]), 'Society');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([BLOCK_COLS, ...blockRows]), 'Blocks');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([FLAT_COLS, ...flatRows]), 'Flats');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(INSTRUCTIONS_ROWS), 'Instructions');
  return wb;
}

export function downloadOnboardExcel(payload: OnboardExcelPayload, fileName: string): void {
  const wb = buildOnboardWorkbook(payload);
  XLSX.writeFile(wb, fileName);
}

export function sanitizeOnboardFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/_+/g, '_').slice(0, 60) || 'Society';
}

export function onboardExportFileName(societyName: string, societyId: number): string {
  const slug = sanitizeOnboardFileName(societyName);
  return `TappyTray_Society_Export_${slug}_${societyId}.xlsx`;
}

/** Sample rows for empty template download */
export function downloadOnboardTemplate(): void {
  downloadOnboardExcel(
    {
      society: {
        name: 'Sunshine Heights',
        location: '123 Main Road, Andheri West',
        city: 'Mumbai',
        state: 'Maharashtra',
        pincode: '400001',
        country: 'India',
        type: 'multiStory',
        flatNoFormat: 'letterNumber',
        latlong: '19.1136,72.8697',
        account_number: '',
        account_ifsc: '',
      },
      blocks: [
        { blockName: 'A', numberOfFloors: 5, customLetter: 'A' },
        { blockName: 'B', numberOfFloors: 6, customLetter: 'B' },
      ],
      flats: [
        {
          block: 'A', flat_number: 'A101', floor: 1, area: 850, flat_type: '2 BHK',
          user_name: 'Ravi Sharma', user_phone: '9876543210', user_email: 'ravi@example.com',
          role_in_flat: 'owner', society_role: 'president',
          two_wheelers: 'MH01AB1234', four_wheelers: 'MH02XY9876',
        },
        {
          block: 'A', flat_number: 'A102', floor: 1, area: 900, flat_type: '2 BHK',
          user_name: '', user_phone: '', user_email: '',
          role_in_flat: '', society_role: '',
          two_wheelers: 'MH01CD5678,MH01EF0011', four_wheelers: '',
        },
        {
          block: 'B', flat_number: 'B201', floor: 2, area: 1100, flat_type: '3 BHK',
          user_name: 'Priya Singh', user_phone: '9812345678', user_email: '',
          role_in_flat: 'owner', society_role: 'treasurer',
          two_wheelers: '', four_wheelers: 'MH03ZZ1111',
        },
        {
          block: 'B', flat_number: 'B202', floor: 2, area: 950, flat_type: '3 BHK',
          user_name: 'Tenant One', user_phone: '9900112233', user_email: '',
          role_in_flat: 'tenant', society_role: 'normal_member',
          two_wheelers: '', four_wheelers: '',
        },
      ],
    },
    'TappyTray_Society_Onboard_Template.xlsx',
  );
}
